import { useLocation } from "wouter";
import { ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { TutorialHint } from "@/components/tutorial-hint";

interface QuickActionCardProps {
  title: string;
  description: string;
  icon: React.ElementType;
  href: string;
  testId: string;
  tutorialHintId?: string;
}

export function QuickActionCard({
  title,
  description,
  icon: Icon,
  href,
  testId,
  tutorialHintId,
}: QuickActionCardProps) {
  const [, navigate] = useLocation();

  const goToHref = () => navigate(href);

  return (
    <Card
      className="hover-elevate cursor-pointer h-full transition-all"
      data-testid={testId}
      role="link"
      tabIndex={0}
      onClick={goToHref}
      onKeyDown={(e) => {
        if (e.currentTarget !== e.target) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          goToHref();
        }
      }}
    >
      <CardContent className="pt-6">
        <div className="flex items-start gap-4">
          <div className="rounded-lg bg-primary/10 p-3 shrink-0">
            <Icon className="h-5 w-5 text-primary" />
          </div>
          <div className="space-y-1 min-w-0">
            <div className="flex items-center gap-1">
              <h3 className="font-semibold">{title}</h3>
              {tutorialHintId && <TutorialHint id={tutorialHintId} />}
            </div>
            <p className="text-sm text-muted-foreground line-clamp-2">
              {description}
            </p>
          </div>
          <ArrowRight className="h-5 w-5 text-muted-foreground shrink-0 ml-auto" />
        </div>
      </CardContent>
    </Card>
  );
}
